import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WorkspaceShell from "@/components/WorkspaceShell";
import { productByKey } from "@/lib/products";
import api from "@/lib/api";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { Loader2, Wand2, Download, Image as ImageIcon, Layers, Palette, Presentation, FileText, Grid } from "lucide-react";
import BrandBrainPanel from "@/components/creative/BrandBrainPanel";
import DocumentsPanel from "@/components/creative/DocumentsPanel";

const PRODUCT = productByKey("creative");

const STYLE_OPTIONS = [
  { v: "modern", l: "Modern minimal" },
  { v: "luxury_gulf", l: "Luxury Gulf" },
  { v: "afrobeat_neon", l: "Afrobeat neon" },
  { v: "editorial", l: "Editorial print" },
  { v: "ramadan", l: "Ramadan campaign" },
];

const LANG_OPTIONS = [
  { v: "en", l: "English" }, { v: "ar", l: "العربية" }, { v: "fr", l: "Français" }, { v: "sw", l: "Kiswahili" },
];

const STARTER_BRIEFS = [
  "Ramadan campaign for a modern Gulf coffee brand, warm gold accents",
  "Eid al-Adha promotion poster for a Nigerian fintech, vibrant colors",
  "Summer travel sale for a Dubai-based airline, luxury cinematic",
  "Launch announcement for a Kenyan organic skincare brand on Instagram",
];

const TABS = [
  { key: "studio",       label: "Studio",        icon: ImageIcon },
  { key: "variants",     label: "4-variant",     icon: Grid },
  { key: "brand",        label: "Brand Brain",   icon: Palette },
  { key: "presentation", label: "Presentations", icon: Presentation },
  { key: "document",     label: "Designed docs", icon: FileText },
];

export default function Creative() {
  const [tab, setTab] = useState("studio");
  const [brief, setBrief] = useState("");
  const [style, setStyle] = useState("luxury_gulf");
  const [language, setLanguage] = useState("en");
  const [brandColor, setBrandColor] = useState("#E1306C");
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantBrief, setVariantBrief] = useState("");
  const [variantLoading, setVariantLoading] = useState(false);
  const [usageBump, setUsageBump] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/creative/assets").then((r) => setAssets(r.data)).catch(() => {});
  }, []);

  const generate = async (text) => {
    const b = (text ?? brief).trim();
    if (!b || loading) return;
    setLoading(true);
    try {
      const r = await api.post("/creative/generate", { brief: b, style, language, brand_color: brandColor });
      setAssets((a) => [r.data, ...a]); setPreview(r.data); setUsageBump((n) => n + 1);
      toast.success("Asset generated");
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const generateVariants = async () => {
    if (!variantBrief.trim() || variantLoading) return;
    setVariantLoading(true); setVariants([]);
    try {
      const r = await api.post("/creative/variants", { brief: variantBrief, variant_count: 4, language });
      setVariants(r.data.variants || []);
      setUsageBump((n) => n + 4);
      toast.success("4 variants generated");
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setVariantLoading(false); }
  };

  const download = (asset) => {
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${asset.image_b64}`;
    link.download = `sanaa-creative-${(asset.id || "asset").slice(0, 8)}.png`;
    link.click();
  };

  return (
    <WorkspaceShell product={PRODUCT} usageRefresh={usageBump}>
      <div className="space-y-5">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`creative-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-sm border text-sm font-medium transition-colors ${
                  active ? "border-foreground bg-card" : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground"
                }`}
                style={active ? { borderColor: PRODUCT.accent } : undefined}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "studio" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 xl:col-span-3">
              <div className="rounded-sm border border-border/60 bg-card p-5 sticky top-24">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Brief</div>
                <textarea data-testid="creative-brief" value={brief} onChange={(e) => setBrief(e.target.value)}
                  rows={5} placeholder="Describe your campaign…"
                  className="w-full resize-none rounded-sm border border-border bg-background p-3 text-sm" />
                <div className="mt-4 space-y-3">
                  <Field label="Style">
                    <select data-testid="creative-style" value={style} onChange={(e) => setStyle(e.target.value)} className="input-sel">
                      {STYLE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </Field>
                  <Field label="Output language">
                    <select data-testid="creative-lang" value={language} onChange={(e) => setLanguage(e.target.value)} className="input-sel">
                      {LANG_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </Field>
                  <Field label="Brand accent">
                    <div className="flex items-center gap-2">
                      <input data-testid="creative-color" type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                        className="h-9 w-12 rounded-sm border border-border bg-background cursor-pointer" />
                      <span className="text-xs font-mono text-muted-foreground">{brandColor}</span>
                    </div>
                  </Field>
                </div>
                <button data-testid="creative-generate" onClick={() => generate()} disabled={loading || !brief.trim()}
                  className="mt-5 inline-flex w-full h-11 items-center justify-center gap-2 rounded-sm text-white text-sm font-medium disabled:opacity-50"
                  style={{ background: PRODUCT.gradient }}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {loading ? "Generating…" : "Generate"}
                </button>
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Try a brief</div>
                  <div className="space-y-1.5">
                    {STARTER_BRIEFS.map((s, i) => (
                      <button key={i} data-testid={`creative-starter-${i}`} onClick={() => setBrief(s)}
                        className="block w-full text-start text-xs rounded-sm bg-secondary/60 hover:bg-secondary px-3 py-2">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 xl:col-span-9">
              <div className="flex items-center justify-between mb-5">
                <h1 className="font-serif text-4xl tracking-tight">Your studio</h1>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{assets.length} {assets.length === 1 ? "asset" : "assets"}</div>
              </div>
              {loading && (
                <div className="mb-4 rounded-sm border border-border/60 bg-card p-6 flex items-center gap-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Sanaa is composing your asset…
                </div>
              )}
              {assets.length === 0 && !loading && (
                <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-14 text-center">
                  <div className="mx-auto h-14 w-14 rounded-sm flex items-center justify-center mb-4"
                    style={{ background: PRODUCT.gradient, color: "white" }}>
                    <ImageIcon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-xl">No assets yet</h3>
                  <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Describe your campaign on the left.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {assets.map((a) => (
                  <button key={a.id} onClick={() => setPreview(a)} data-testid={`creative-asset-${a.id}`}
                    className="group relative rounded-sm overflow-hidden border border-border/60 bg-card hover:border-foreground/30">
                    <div className="aspect-square overflow-hidden">
                      <img src={`data:image/png;base64,${a.image_b64}`} alt={a.caption}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    </div>
                    <div className="p-3 text-start"><div className="text-xs text-muted-foreground line-clamp-2">{a.caption}</div></div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "variants" && (
          <div className="space-y-4" data-testid="creative-variants-panel">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Multi-variant</div>
              <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><Grid className="h-6 w-6" /> Try 4 looks at once</h2>
              <div className="text-xs text-muted-foreground mt-1">One brief → four parallel generations across different styles. Each variant counts as one Creative call.</div>
            </div>
            <div className="rounded-sm border border-border/60 bg-card p-4 flex gap-2">
              <input value={variantBrief} onChange={(e) => setVariantBrief(e.target.value)} placeholder="e.g. Eid greeting for a luxury jewelry brand"
                data-testid="variant-brief" className="flex-1 h-10 rounded-sm border border-border bg-background px-3 text-sm" />
              <button onClick={generateVariants} disabled={variantLoading || !variantBrief.trim()}
                data-testid="variant-generate"
                className="h-10 px-4 rounded-sm text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2"
                style={{ background: PRODUCT.gradient }}>
                {variantLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
                Generate 4
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {variants.map((v, i) => (
                <button key={i} data-testid={`variant-${i}`} onClick={() => setPreview({ ...v, id: `variant-${i}` })}
                  className="rounded-sm overflow-hidden border border-border/60 bg-card hover:border-foreground/30">
                  <div className="aspect-square overflow-hidden">
                    <img src={`data:image/png;base64,${v.image_b64}`} alt={v.style} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3 text-start">
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: PRODUCT.accent }}>{v.style}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{v.caption}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "brand"        && <BrandBrainPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}
        {tab === "presentation" && <DocumentsPanel  accent={PRODUCT.accent} gradient={PRODUCT.gradient} kind="presentation" />}
        {tab === "document"     && <DocumentsPanel  accent={PRODUCT.accent} gradient={PRODUCT.gradient} kind="document" />}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-4xl w-full bg-card border border-border rounded-sm overflow-hidden grid grid-cols-1 md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-square overflow-hidden bg-secondary">
              <img src={`data:image/png;base64,${preview.image_b64}`} alt={preview.caption} className="w-full h-full object-cover" />
            </div>
            <div className="p-7 flex flex-col">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Sanaa Creative</div>
              <h2 className="font-display text-2xl mt-2">Generated asset</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{preview.caption}</p>
              <div className="mt-auto pt-7 flex gap-2">
                <button data-testid="creative-download" onClick={() => download(preview)}
                  className="inline-flex h-10 items-center gap-2 rounded-sm bg-foreground px-4 text-sm text-background">
                  <Download className="h-4 w-4" /> Download PNG
                </button>
                <button onClick={() => setPreview(null)} className="inline-flex h-10 items-center rounded-sm border border-border px-4 text-sm">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.input-sel{width:100%;height:36px;padding:0 10px;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:2px;font-size:13px;color:hsl(var(--foreground));outline:none;}`}</style>
    </WorkspaceShell>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span><div className="mt-1.5">{children}</div></label>;
}
